import React, { useState, useEffect } from 'react';
import { X, Calendar, Save, XCircle, AlertCircle, Edit3, Clock, Users, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Turma } from '../services/firebaseService';
import { User } from '../types/User';

interface TurmaModalProps {
  turma: Turma | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (turmaData: Partial<Turma>) => Promise<void>;
  professores: User[];
}

const TurmaModal: React.FC<TurmaModalProps> = ({ turma, isOpen, onClose, onSave, professores }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(!turma);
  const [formData, setFormData] = useState<Partial<Turma>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (turma) {
      setFormData({
        nome: turma.nome || '',
        tipoTurma: turma.tipoTurma || 'regular',
        nivelId: turma.nivelId || '',
        horarioInicio: turma.horarioInicio || '',
        horarioFim: turma.horarioFim || '',
        limiteAlunos: turma.limiteAlunos || 0,
        piscina: turma.piscina || '',
        observacoes: turma.observacoes || '',
        ativo: turma.ativo !== undefined ? turma.ativo : true,
        professoresIds: Array.isArray(turma.professoresIds) ? turma.professoresIds : [],
        diasSemana: Array.isArray(turma.diasSemana) ? turma.diasSemana : []
      });
      setIsEditing(false);
    } else {
      setFormData({
        nome: '',
        tipoTurma: 'regular',
        nivelId: '',
        horarioInicio: '',
        horarioFim: '',
        limiteAlunos: 0,
        piscina: '',
        observacoes: '',
        ativo: true,
        professoresIds: [],
        diasSemana: []
      });
      setIsEditing(true);
    }
    setErrors({});
  }, [turma, isOpen]);

  const diasSemanaOptions = [
    { value: 'Segunda', label: 'Segunda-feira' },
    { value: 'Terça', label: 'Terça-feira' },
    { value: 'Quarta', label: 'Quarta-feira' },
    { value: 'Quinta', label: 'Quinta-feira' },
    { value: 'Sexta', label: 'Sexta-feira' },
    { value: 'Sábado', label: 'Sábado' },
    { value: 'Domingo', label: 'Domingo' }
  ];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.horarioInicio) {
      newErrors.horarioInicio = 'Horário de início é obrigatório';
    }

    if (!formData.horarioFim) {
      newErrors.horarioFim = 'Horário de fim é obrigatório';
    }

    if (formData.limiteAlunos === undefined || formData.limiteAlunos < 1) {
      newErrors.limiteAlunos = 'Limite de alunos deve ser maior que zero';
    }

    if (!formData.nivelId) {
      newErrors.nivelId = 'Nível é obrigatório';
    }

    if (!Array.isArray(formData.diasSemana) || formData.diasSemana.length === 0) {
      newErrors.diasSemana = 'Selecione pelo menos um dia da semana';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: Partial<Turma>) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDiaSemanaToggle = (dia: string) => {
    const currentDias = Array.isArray(formData.diasSemana) ? formData.diasSemana : [];
    const newDias = currentDias.includes(dia)
      ? currentDias.filter(d => d !== dia)
      : [...currentDias, dia];
    
    handleInputChange('diasSemana', newDias);
  };

  const handleProfessorToggle = (professorId: string) => {
    const currentProfessores = Array.isArray(formData.professoresIds) ? formData.professoresIds : [];
    const newProfessores = currentProfessores.includes(professorId)
      ? currentProfessores.filter(id => id !== professorId)
      : [...currentProfessores, professorId];
    
    handleInputChange('professoresIds', newProfessores);
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...(turma || {}),
        ...formData
      });
      setIsEditing(false);
      if (!turma) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar turma:', error);
      setErrors({ submit: 'Erro ao salvar turma. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (turma) {
      setFormData({
        nome: turma.nome || '',
        tipoTurma: turma.tipoTurma || 'regular',
        nivelId: turma.nivelId || '',
        horarioInicio: turma.horarioInicio || '',
        horarioFim: turma.horarioFim || '',
        limiteAlunos: turma.limiteAlunos || 0,
        piscina: turma.piscina || '',
        observacoes: turma.observacoes || '',
        ativo: turma.ativo !== undefined ? turma.ativo : true,
        professoresIds: Array.isArray(turma.professoresIds) ? turma.professoresIds : [],
        diasSemana: Array.isArray(turma.diasSemana) ? turma.diasSemana : []
      });
      setIsEditing(false);
    } else {
      onClose();
    }
    setErrors({});
  };

  if (!isOpen) return null;

  const isCreating = !turma;
  const displayName = turma?.nome || 'Nova Turma';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {isCreating ? 'Nova Turma' : (isEditing ? 'Editar Turma' : 'Detalhes da Turma')}
                </h2>
                {turma && <p className="text-sm text-gray-500 dark:text-gray-400">ID: {turma.id}</p>}
              </div>
            </div>
            <div className="flex items-center gap-x-2">
              {!isEditing && !isCreating ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  Editar
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                    disabled={saving}
                  >
                    <XCircle className="h-4 w-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2"
                    disabled={saving}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={saving}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Calendar className="h-5 w-5" />
                Informações Básicas
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome da Turma <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Ex: Natação Infantil - Manhã"
                    />
                    {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{turma?.nome || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo de Turma
                </label>
                {isEditing || isCreating ? (
                  <select
                    value={formData.tipoTurma}
                    onChange={(e) => handleInputChange('tipoTurma', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="regular">Regular</option>
                    <option value="intensiva">Intensiva</option>
                    <option value="particular">Particular</option>
                    <option value="hidroginastica">Hidroginástica</option>
                  </select>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100 capitalize">{turma?.tipoTurma || 'Não definido'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nível <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <select
                      value={formData.nivelId}
                      onChange={(e) => handleInputChange('nivelId', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.nivelId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <option value="">Selecione um nível</option>
                      <option value="iniciante">Iniciante</option>
                      <option value="intermediario">Intermediário</option>
                      <option value="avancada">Avançada</option>
                    </select>
                    {errors.nivelId && <p className="text-red-500 text-xs mt-1">{errors.nivelId}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">
                    {formData.nivelId === 'iniciante' ? 'Iniciante' :
                     formData.nivelId === 'intermediario' ? 'Intermediário' :
                     formData.nivelId === 'avancada' ? 'Avançada' :
                     turma?.nivelId || 'Não definido'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Piscina
                </label>
                {isEditing || isCreating ? (
                  <input
                    type="text"
                    value={formData.piscina}
                    onChange={(e) => handleInputChange('piscina', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: Piscina Principal"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{turma?.piscina || 'Não definida'}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Clock className="h-5 w-5" />
                Horários e Capacidade
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Horário Início <span className="text-red-500">*</span>
                  </label>
                  {isEditing || isCreating ? (
                    <>
                      <input
                        type="time"
                        value={formData.horarioInicio}
                        onChange={(e) => handleInputChange('horarioInicio', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors.horarioInicio ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      {errors.horarioInicio && <p className="text-red-500 text-xs mt-1">{errors.horarioInicio}</p>}
                    </>
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">{turma?.horarioInicio || 'Não definido'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Horário Fim <span className="text-red-500">*</span>
                  </label>
                  {isEditing || isCreating ? (
                    <>
                      <input
                        type="time"
                        value={formData.horarioFim}
                        onChange={(e) => handleInputChange('horarioFim', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors.horarioFim ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      {errors.horarioFim && <p className="text-red-500 text-xs mt-1">{errors.horarioFim}</p>}
                    </>
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">{turma?.horarioFim || 'Não definido'}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Limite de Alunos <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="number"
                      min="1"
                      value={formData.limiteAlunos}
                      onChange={(e) => handleInputChange('limiteAlunos', parseInt(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.limiteAlunos ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {errors.limiteAlunos && <p className="text-red-500 text-xs mt-1">{errors.limiteAlunos}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{turma?.limiteAlunos || 0} alunos</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                {isEditing || isCreating ? (
                  <select
                    value={formData.ativo ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('ativo', e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent max-w-xs"
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </select>
                ) : (
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    turma?.ativo 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {turma?.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Dias da Semana */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
              <Calendar className="h-5 w-5" />
              Dias da Semana <span className="text-red-500">*</span>
            </h3>
            {isEditing || isCreating ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {diasSemanaOptions.map((dia) => {
                  const isSelected = Array.isArray(formData.diasSemana) && formData.diasSemana.includes(dia.value);
                  return (
                    <button
                      key={dia.value}
                      type="button"
                      onClick={() => handleDiaSemanaToggle(dia.value)}
                      className={`px-4 py-2 rounded-md border-2 transition-colors ${
                        isSelected
                          ? 'bg-primary-50 border-primary-500 text-primary-700 font-medium'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-300'
                      }`}
                    >
                      {dia.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Array.isArray(turma?.diasSemana) && turma.diasSemana.length > 0 ? (
                  turma.diasSemana.map((dia) => {
                    const diaOption = diasSemanaOptions.find(d => d.value === dia);
                    return diaOption ? (
                      <span key={dia} className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                        {diaOption.label}
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Sem dias definidos</span>
                )}
              </div>
            )}
            {errors.diasSemana && <p className="text-red-500 text-xs mt-1">{errors.diasSemana}</p>}
          </div>

          {/* Professores */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
              <Users className="h-5 w-5" />
              Professores
            </h3>
            {isEditing || isCreating ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-md">
                {professores.map((professor) => {
                  const isSelected = Array.isArray(formData.professoresIds) && formData.professoresIds.includes(professor.id);
                  return (
                    <label
                      key={professor.id}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleProfessorToggle(professor.id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{professor.nome || 'Sem nome'}</span>
                    </label>
                  );
                })}
                {professores.length === 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 col-span-2">Nenhum professor cadastrado</span>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Array.isArray(turma?.professoresIds) && turma.professoresIds.length > 0 ? (
                  turma.professoresIds.map((professorId) => {
                    const professor = professores.find(p => p.id === professorId);
                    return professor ? (
                      <span key={professorId} className="inline-flex items-center gap-x-1 px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                        <Users className="h-3 w-3" />
                        {professor.nome || 'Sem nome'}
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nenhum professor vinculado</span>
                )}
              </div>
            )}
          </div>

          {/* Observações */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
              <MapPin className="h-5 w-5" />
              Observações
            </h3>
            {isEditing || isCreating ? (
              <textarea
                value={formData.observacoes || ''}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Adicione observações sobre a turma..."
              />
            ) : (
              <p className="text-gray-900 dark:text-gray-100">{turma?.observacoes || 'Sem observações'}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurmaModal;

